import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DefaultButton } from '../common/components/DefaultButton.js';
import PanelTitle from '../common/components/PanelTitle.js';
import { SortDialog } from '../common/components/Sort.js';
import TextInput from '../common/components/TextInput.js';
import useClientConfig from '../common/hooks/clientConfigHook.js';
import useCredentials from '../common/hooks/credentialsHook.js';
import useNetwork from '../common/hooks/networkHook.js';
import useWebsocket from '../common/hooks/websocketHook.js';
import { ReactNodeDialog } from '../common/utils/dialogUtlities.js';
import { dynamicFilter, dynamicSort } from '../common/utils/tableUtilities.js';
import { useAllJobAssistanceMap } from '../reducers/jobAssistanceSlice.js';
import { active, compareJobFilters, insertJobFilter, matches, removeJobFilter, useJobFilters } from '../reducers/jobFilterSlice.js';
import { canAccessJob, resetNewJob, useActiveJobs } from "../reducers/jobSlice.js";
import { useCADPopout, useSelectedJobid, useSelectedRequestableJobid } from '../utils/CADStateProvider.js';
import { JobFilterModal } from './JobFilterModal.js';
import JobListEntry from './JobListEntry.js';
import JobSearch from './JobSearchPanel.js';
import { useRequestableJobs } from '../reducers/requestableJobsSlice.js';
import { checkEnabled } from '../utils/enableUtilities.js';
import { distinct } from '../common/utils/arrayUtilities.js';
import { usePriorities } from '../reducers/prioritySlice.js';
import { readField } from '../common/utils/objectUtilities.js';
import { useSessionState } from '../common/hooks/sessionStateHook.js';

// TODO: predefined filters
const defaultSort = ['-pinned', '-new', 'priority', 'displayid'];

export const JobListPanel = ({ jobs, assistance, title, fixed, hasJobAccess }) => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const priorities = usePriorities();
    const network = useNetwork();
    const { register } = useWebsocket();
    const popout = useCADPopout();
    const [sort, setSort] = useSessionState('jobListPanelSort', [...defaultSort]);
    const [updated, setUpdated] = useState({});
    const [created, setCreated] = useState({});
    const [pinned, setPinned] = useState({});
    const [watched, setWatched] = useState({});
    const [searchText, setSearchText] = useState('');
    const [showSearch, setShowSearch] = useState(false);
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [showSortModal, setShowSortModal] = useState(false);
    const { selectedJobId, setSelectedJobId } = useSelectedJobid();
    const { selectedRequestableJobId, setSelectedRequestableJobId } = useSelectedRequestableJobid();

    const sortForEdit = useMemo(() => sort.slice(1), [sort]);

    const filters = useJobFilters();
    const [jobFilters, setJobFilters] = useState(filters);
    const [includeAssistance, setIncludeAssistance] = useState(true);

    useEffect(() => setJobFilters(jobFilters => jobFilters.length === 0 ? filters : jobFilters), [filters]);

    useEffect(() => {
        if (selectedJobId) {
            setCreated(created => ({ ...created, [selectedJobId]: false }));
            setUpdated(updated => ({ ...updated, [selectedJobId]: false }));
        }
    }, [selectedJobId]);

    const sortFields = useMemo(() => ({
        new: t('new'),
        displayid: t('job', { count: 1 }),
        priority: t('priority', { count: 1 }),
        status: t('status', { count: 1 }),
        lastupdate: t('updated'),
        location: t('location', { count: 1 }),
        type: t('type', { count: 1 }),
    }), [t]);

    const jobUpdateHandler = useCallback(job => {
        if (!selectedJobId || job.jobid.toString() !== selectedJobId.toString()) {
            if (watched[job.jobid.toString()]) {
                setCreated(created => ({ ...created, [job.jobid.toString()]: true }));
            } else if (!created[job.jobid.toString()]) {
                setUpdated(updated => ({ ...updated, [job.jobid.toString()]: job.status !== 'Closed' }));
            }
        }
    }, [selectedJobId, watched, created]);

    const jobCreatedHandler = useCallback(job => {
        if ((!selectedJobId || job.jobid.toString() !== selectedJobId.toString()) && jobs.every(j => j.jobid !== job.jobid.toString())) {
            setCreated({ ...created, [job.jobid.toString()]: true});
        }
    }, [selectedJobId, created, jobs]);

    useEffect(() => {
        return register('jobUpdated', jobUpdateHandler);
    }, [register, jobUpdateHandler])

    useEffect(() => {
        return register('jobCreated', jobCreatedHandler);
    }, [register, jobCreatedHandler])

    const doJobSelect = useCallback(job => {
        if (!job) {
            return;
        }
        const jobid = job.jobid;
        
        if (checkEnabled(clientConfig.requestableJobs.enable, job) && (hasJobAccess && !hasJobAccess(job))) {
            setSelectedRequestableJobId(jobid);
            return;
        }
        setSelectedJobId(selectedJobId !== jobid ? jobid : undefined);
    }, [clientConfig, setSelectedJobId, selectedJobId, hasJobAccess, setSelectedRequestableJobId]);

    const displayJobs = useMemo(() => {
        let displayJobs = [ ...jobs ];
        displayJobs.sort(dynamicSort(sort, (obj, field) => {
            if (field === 'priority') {
                return priorities.find(p => p.internalid === obj.priority)?.value;
            }
            if (field === 'pinned') {
                return pinned[obj.jobid] ? 1 : 0;
            }
            if (field === 'new') {
                return created[obj.jobid] ? 1 : 0;
            }
            if (field === 'displayid') {
                if (Number.isNaN(Number.parseInt(obj.displayid))) {
                    return obj.displayid
                }
                return parseInt(obj.displayid);
            }
            return readField(obj, field);
        }));
        if (searchText) {
            displayJobs = dynamicFilter(displayJobs, searchText);
        }
        if (jobFilters) {
            displayJobs = displayJobs?.filter(j => matches(jobFilters, j, includeAssistance && assistance?.length > 0 ? assistance.filter(a => a.jobid === j.jobid) : [], clientConfig.enableSites, clientConfig.enableLevels));
        }
        return displayJobs;
    }, [jobs, assistance, searchText, jobFilters, clientConfig.enableSites, clientConfig.enableLevels, sort, includeAssistance, priorities, pinned, created]);

    const showSites = useMemo(() => clientConfig.enableSites && distinct(displayJobs ?? [], 'site')?.length > 1, [clientConfig.enableSites, displayJobs]);
    const showLevels = useMemo(() => clientConfig.enableLevels && distinct(displayJobs ?? [], 'level')?.length > 1, [clientConfig.enableLevels, displayJobs]);
    const showEvents = useMemo(() => clientConfig.enableEvents && distinct(displayJobs ?? [], 'event')?.length > 1, [clientConfig.enableEvents, displayJobs]);

    const handleClick = useCallback((job) => doJobSelect(job), [doJobSelect]);
    const handleCreated = useCallback((job, value) => setCreated(created => ({...created, [job.jobid]: value})), []);
    const handleUpdated = useCallback((job, value) => setUpdated(updated => ({...updated, [job.jobid]: value})), []);
    const handlePinned = useCallback((job, value) => setPinned(pinned => ({ ...pinned, [job.jobid]: value })), []);
    const handleWatched = useCallback((job, value) => setWatched(watched => ({...watched, [job.jobid] : value})), []);

    const listEntries = displayJobs.map(job => fixed ? 
            <JobListEntry key={'ajl_' + job.jobid} job={job} showSites={showSites} showLevels={showLevels} showEvents={showEvents}></JobListEntry>
            : <JobListEntry key={'ajl_' + job.jobid} job={job} showSites={showSites} showLevels={showLevels} showEvents={showEvents} selected={job.jobid === selectedRequestableJobId || job.jobid === selectedJobId} OnClick={handleClick} created={!!created[job.jobid]} updated={!!updated[job.jobid]} pinned={!!pinned[job.jobid]} watched={!!watched[job.jobid]} SetCreated={handleCreated} SetUpdated={handleUpdated} SetPinned={handlePinned} SetWatched={handleWatched}></JobListEntry>)

    const handleClose = (result) => {
        if (result) {
            const { filters: resultFilters, persist: resultPersist, includeAssistance: resultIncludeAssistance } = result;
            if (resultPersist) {
                resultFilters.forEach(rf => {
                    const jf = filters.find(jf => compareJobFilters(jf, rf));
                    if (rf.kind === 'Zone') {
                        if (jf.selected === rf.selected) {
                            if (rf.selected) {
                                removeJobFilter(network, { type: rf.type, kind: rf.kind });
                            } else {
                                insertJobFilter(network, { type: rf.type, kind: rf.kind });
                            }
                        }
                    } else if (jf.selected !== rf.selected) {
                        if (rf.selected) {
                            insertJobFilter(network, { type: rf.type, kind: rf.kind });
                        } else {
                            removeJobFilter(network, { type: rf.type, kind: rf.kind });
                        }
                    }
                })
            }
            setJobFilters(resultFilters.map(rf => {
                if (rf.kind === 'Zone') {
                    return {...rf, selected: !rf.selected };
                } else {
                    return rf;
                }
            }));
            if (includeAssistance !== resultIncludeAssistance) {
                setIncludeAssistance(resultIncludeAssistance);
            }
        }
        setShowFilterModal(false);
    }

    return (<div id="activeJobPanel" className="px-2">
        <JobFilterModal filters={jobFilters} includeAssistance={includeAssistance} handleClose={handleClose} showModal={showFilterModal}></JobFilterModal>
        <SortDialog sort={sortForEdit} fields={sortFields} defaultOrder={defaultSort} show={showSortModal} title={t('jobSort')} onClose={(newSort) => { if (newSort) { setSort(['-pinned',...newSort]) } setShowSortModal(false); } } placeholder={t('sort')}></SortDialog>
        <ReactNodeDialog title={t('jobSearch')} showModal={showSearch} size="xlg" onClose={() => setShowSearch(false)}><div className="w-100" style={{height: 'calc(100vh - 220px)'}}><JobSearch onSelect={job => { setSelectedJobId(job.jobid); setShowSearch(false); }}></JobSearch></div></ReactNodeDialog>
        <PanelTitle title={title} guide={78543} onPopout={() => popout('activeJobPanel')}>
            <div className="flex-grow-1" style={{ minWidth: '15rem' }}>
                <TextInput value={searchText} live={true} hideLabel={true} placeholder={t('filter', { count: 1 })} onChange={setSearchText} prependCtrl={<>
                    <DefaultButton kind="sort" hideLabel={true} onClick={() => setShowSortModal(true)}></DefaultButton>
                    <DefaultButton kind="filter" variant={active(jobFilters) ? 'success' : 'default'} hideLabel={true} onClick={() => setShowFilterModal(true)}></DefaultButton>
                </>} appendCtrl={<DefaultButton kind="search" onClick={() => setShowSearch(true)}></DefaultButton>}></TextInput>
            </div>
            <DefaultButton className="chrono-inline-nowrap" kind="new" onClick={() => { resetNewJob(network); setSelectedJobId() }}></DefaultButton>
        </PanelTitle>
        {listEntries}
        </div>);
}
const ActiveJobPanel = () => {
    const { t } = useTranslation();
    const credentials = useCredentials();
    const clientConfig = useClientConfig();
    const jobs = useActiveJobs();
    const requestableJobs = useRequestableJobs();
    const assistanceMap = useAllJobAssistanceMap();

    const assistance = useMemo(() => Object.values(assistanceMap).flat(), [assistanceMap])
    
    const mergedJobs = useMemo(() => {
        const jobIds = new Set(jobs.map(job => job.jobid));
        return jobs.concat(requestableJobs.filter(job => !jobIds.has(job.jobid)));
    }, [jobs, requestableJobs]);
    

    const jobAccessCheck = useCallback(job => {
        const jobAssistance = assistanceMap[job.jobid] ?? [];        
        return canAccessJob(credentials, clientConfig, job, jobAssistance)
    }, [assistanceMap, clientConfig, credentials]);

    return <JobListPanel title={t('activeJobs')} jobs={mergedJobs} assistance={assistance} hasJobAccess={jobAccessCheck}></JobListPanel>
}

export default ActiveJobPanel;